import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ErrorHandler, Injector, OnDestroy, OnInit, ViewChild, inject, signal } from '@angular/core'
import { disabled, FieldTree, form, max, min, required } from '@angular/forms/signals'
import { ActivatedRoute, Router } from '@angular/router'

import { Kayttaja, KayttajanTiedot, TositteenLisaysprosessinOsanTunniste } from '@lemon/core-firebase/model/kayttaja'
import {
  FirestoreTosite,
  FirestoreTositteenAlkuperainenTiedosto,
  FirestoreTositteenKuva,
  Kuitti,
  MYYNTITOSITE_MAKSUTAPA_ID,
  Maksutapa,
  PALKKATOSITE_MAKSUTAPA_ID,
  SAHKOISET_LASKUT_MAKSUTAPA_ID,
  TILIOTETOSITE_MAKSUTAPA_ID,
  TositeAutokirjaustyyppi,
} from '@lemon/core-firebase/model/tosite'
import { LocalMonth } from '@lemon/core/model/common'

import { TositeMuokkaaComponentData } from '../../_angular/_resolvers/tosite.resolve'
import { KayttajaService } from '../../_angular/service/kayttaja.service'
import { AlvPortaatJaoletus, PurettuTiedosto, TositeService } from '../../_angular/service/tosite/tosite.service'

import { KopioijaPalvelu } from '@lemon/core-firebase/service/kopioija.service'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'
import { TositeDatasourceService } from '../../_angular/service/tosite/tosite-datasource.service'
import { DragAndDropService } from '../../_jaettu-angular/service/drag-and-drop.service'
import { arvonMuutokset, asetaArvo } from '../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from '../../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'
import { SimplifiedUploadData, TiedostojenLataamisService } from '../../_jaettu-angular/service/tiedostojen-lataamis.service'
import { TimestampService } from '../../_jaettu-angular/service/timestamp-service'
import { TositeKuvaCacheService } from '../kuvat/tosite-kuva-cache.service'

import { FileSystemFileEntry } from '../../_jaettu-angular/file-drop/file-drop-dom.types'
import { FileDropEntry } from '../../_jaettu-angular/file-drop/file-drop-entry'

import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { BehaviorSubject, Observable, Subject, combineLatest, of as observableOf } from 'rxjs'
import { map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators'

interface TositeLomake {
  maksutapa: number | null
  tositteenPvm: Date | null
  summa: number | null
  lisatiedot: string
  alvVahennysoikeus: number | null
}

export interface KuvaAvainJaUrl {
  avain: string
  url: string
  lukittu: boolean
  jarjestys: number
}
export interface KuvaAvainUrlJaJarjestys {
  avain: string
  url: string
  jarjestys: number
}

export interface TositteenPdfKuvatData {
  kuvat: KuvaAvainUrlJaJarjestys[]
}

@Component({
  templateUrl: './tositteen.muokkaus.component.html',
  styleUrls: ['./tositteen.muokkaus.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TositteenMuokkausComponent implements OnInit, OnDestroy {
  maxDate: Date = new Date(2099, 11, 31)
  minDate: Date = new Date(2010, 0, 1)

  @ViewChild('fileInput', { static: true }) fileInput: ElementRef<HTMLInputElement>

  private ngUnsubscribe = new Subject<void>()

  private _uploadTasksSubject: BehaviorSubject<SimplifiedUploadData[]> = new BehaviorSubject([])

  lomake: FieldTree<TositeLomake> = null
  maksutapa: FieldTree<number | null>
  tositteenPvm: FieldTree<Date | null>
  summa: FieldTree<number | null>
  lisatiedot: FieldTree<string>
  alvVahennysoikeus: FieldTree<number | null>

  private readonly _injector = inject(Injector)
  private readonly _maksutapaPoissa = signal(false)
  private readonly _tositteenPvmPoissa = signal(false)
  private readonly _summaPoissa = signal(false)
  private readonly _alvVahennysoikeusPoissa = signal(false)

  kuvavirheAvain: string = ''
  kuvavirheParametrit: Object = {}

  hideSum: boolean = false
  showPvm: boolean = true
  dateSelectionType: 'day' | 'month' = 'day'
  isDraggingObservable: Observable<boolean>
  pvmText: string
  summaText: string

  kuitti: FirestoreTosite = null
  alvPortaat: number[] = null
  virheviestiLatausOnKesken: string = null
  virheviestiPysyva: string = null
  virheviestiTallennus: string = null
  virheviestiEiKuvia: string = null
  muokataan: boolean = false
  showMaksutavat: boolean = true

  kuvatDisplay: KuvaAvainJaUrl[] = []
  pdfKuvatDisplay: { alkuperaisenAvain: string; kuvat: KuvaAvainUrlJaJarjestys[]; lukittu: boolean }[] = []

  newAlkuperaisetCache: FirestoreTositteenAlkuperainenTiedosto[] = []
  newKuvatCache: FirestoreTositteenKuva[] = []

  private onkoVielaSelvitettaviaListalla: boolean
  private _selvityspyynnonAvain: string | undefined
  private startingMaksutapa: number
  private startingMonth: LocalMonth

  alvPortaatObservable: Observable<AlvPortaatJaoletus>

  donePercentageObservable: Observable<number>
  latausvirheetObservable: Observable<string[]>
  kirjanpitajanKommentti: string
  maksajaTaiMaksunSaaja: string

  private oletusAlv: number = null
  private poistetuksiMerkitytKuvat: string[] = []

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _tositeService: TositeService,
    private _ladataanService: LadataanService,
    private _dateService: DateService,
    private _validationService: FormValidationService,
    private _timestampService: TimestampService,
    private _currencyService: CurrencyService,
    private _dndService: DragAndDropService,
    private _tiedostojenLataamisService: TiedostojenLataamisService,
    private _translationService: LemonTranslationService,
    private _errorHandler: ErrorHandler,
    private _kayttajaService: KayttajaService,
    private _tositeDatasourceService: TositeDatasourceService,
    private _tositeKuvaCacheService: TositeKuvaCacheService,
    private _changeDetectionRef: ChangeDetectorRef,
    private _copyService: KopioijaPalvelu,
    private _firebaseLemonaid: FirebaseLemonaid,
  ) {
    this.maxDate = this._dateService.lisaaKuukausia(new Date(), 12)
    this.minDate = this._dateService.lisaaKuukausia(new Date(), -48)
  }

  ngOnInit() {
    const tositeDataObservable = this._route.data.pipe(map(routeData => (routeData?.data || null) as TositeMuokkaaComponentData))

    combineLatest([this._kayttajaService.kayttajanTiedotObservable, tositeDataObservable])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: async ([kayttajanTiedot, data]) => {
          this.poistetuksiMerkitytKuvat = []

          this.onkoVielaSelvitettaviaListalla = data?.onkoVielaSelvitettavia
          this._selvityspyynnonAvain = data?.selvityspyynnonAvain
          this.startingMaksutapa = data.tosite.maksutapa
          this.startingMonth = { year: data.tosite.localPvm.year, month: data.tosite.localPvm.month }

          if (data?.tosite) {
            this.kuvatDisplay = []
            const pdfKuvatMap: { [alkuperaisenAvain: string]: KuvaAvainUrlJaJarjestys[] } = {}
            for (const kuvaData of Object.values(data.tosite.kuvat || {})) {
              if (kuvaData.poistettu) {
                continue
              }
              if (
                kuvaData.alkuperaisenAvain &&
                data.tosite.alkuperaiset &&
                data.tosite.alkuperaiset[kuvaData.alkuperaisenAvain] &&
                data.tosite.alkuperaiset[kuvaData.alkuperaisenAvain].fileEnding &&
                data.tosite.alkuperaiset[kuvaData.alkuperaisenAvain].fileEnding.toLocaleLowerCase() === 'pdf'
              ) {
                const pdfKuvanUrl = this._tositeKuvaCacheService.annaKuvanUrl(kayttajanTiedot, data.tosite, kuvaData, 'full')
                if (!pdfKuvatMap[kuvaData.alkuperaisenAvain]) {
                  pdfKuvatMap[kuvaData.alkuperaisenAvain] = []
                }
                pdfKuvatMap[kuvaData.alkuperaisenAvain].push({ avain: kuvaData.avain, url: pdfKuvanUrl, jarjestys: kuvaData.jarjestys })
              } else {
                const tavallisenKuvanUrl = this._tositeKuvaCacheService.annaKuvanUrl(kayttajanTiedot, data.tosite, kuvaData, 'full')
                this.kuvatDisplay.push({ avain: kuvaData.avain, url: tavallisenKuvanUrl, jarjestys: kuvaData.jarjestys, lukittu: !!kuvaData?.lukittu })
              }
            }

            const avaimet = Object.keys(pdfKuvatMap || {}).sort((a, b) => a.localeCompare(b))
            for (const alkuperaisenAvain of avaimet) {
              const alkuperainen = data.tosite.alkuperaiset[alkuperaisenAvain]
              const kuvat = pdfKuvatMap[alkuperaisenAvain].sort((a, b) => {
                return a.jarjestys - b.jarjestys
              })
              this.pdfKuvatDisplay.push({ alkuperaisenAvain: alkuperaisenAvain, kuvat: kuvat, lukittu: !!alkuperainen?.lukittu })
            }
            this.kuitti = this._copyService.cloneDeep(data.tosite)

            this.kuvatDisplay.sort((a, b) => {
              return a.jarjestys - b.jarjestys
            })

            this._maksutapaPoissa.set(false)
            this._tositteenPvmPoissa.set(false)
            this._summaPoissa.set(false)
            this._alvVahennysoikeusPoissa.set(false)
            this.lomake = form(
              signal<TositeLomake>({
                maksutapa: null,
                tositteenPvm: this._dateService.timestampToDate(this.kuitti.pvm),
                summa: this.kuitti.sum ?? this.kuitti.summa,
                lisatiedot: this.kuitti.selite ?? '',
                alvVahennysoikeus: this.kuitti.alvVahennysoikeus,
              }),
              polku => {
                required(polku.maksutapa)
                disabled(polku.maksutapa, () => this._maksutapaPoissa())
                required(polku.tositteenPvm)
                disabled(polku.tositteenPvm, () => this._tositteenPvmPoissa())
                required(polku.summa)
                min(polku.summa, -999999.99)
                max(polku.summa, 999999.99)
                disabled(polku.summa, () => this._summaPoissa())
                disabled(polku.alvVahennysoikeus, () => this._alvVahennysoikeusPoissa())
              },
              { injector: this._injector },
            )
            this.maksutapa = this.lomake.maksutapa
            this.tositteenPvm = this.lomake.tositteenPvm
            this.summa = this.lomake.summa
            this.lisatiedot = this.lomake.lisatiedot
            this.alvVahennysoikeus = this.lomake.alvVahennysoikeus

            arvonMuutokset(this.maksutapa, { injector: this._injector })
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe(value => {
                this.kuitti.maksutapa = value
                this.setDisplayForSelectedMaksutapa(value)
              })

            // Delay value set to start first emit
            asetaArvo(this.maksutapa, this.kuitti.maksutapa)

            if (this.kuitti.x) {
              // If selvitettävä tosite, don't allow changing maksutapa, sum or date
              this.showMaksutavat = false
              this._maksutapaPoissa.set(true)
              this._summaPoissa.set(true)
              this._tositteenPvmPoissa.set(true)
              this._alvVahennysoikeusPoissa.set(true)
            }

            if (this.kuitti.maksutapa === SAHKOISET_LASKUT_MAKSUTAPA_ID) {
              // If sähköinen lasku, don't allow changing maksutapa, sum or date
              this.showMaksutavat = false
              this._maksutapaPoissa.set(true)
              this._summaPoissa.set(true)
              this._tositteenPvmPoissa.set(true)
              this._alvVahennysoikeusPoissa.set(true)
            }

            arvonMuutokset(this.tositteenPvm, { injector: this._injector })
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe((value: Date) => {
                this.kuitti.pvm = this._timestampService.dateToTimestamp(value)
                this.kuitti.localPvm = this._dateService.dateToLocalDate(value)
                this.kuitti.p = this._dateService.dateToNumber(value)
              })

            arvonMuutokset(this.summa, { injector: this._injector })
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe(value => {
                this.kuitti.summa = Math.abs(value)
                this.kuitti.sum = value
              })
            arvonMuutokset(this.lisatiedot, { injector: this._injector })
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe(value => {
                this.kuitti.selite = value
              })
            arvonMuutokset(this.alvVahennysoikeus, { injector: this._injector })
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe(value => {
                this.kuitti.alvVahennysoikeus = value
              })

            this.kirjanpitajanKommentti = this.kuitti.m || ''
            this.maksajaTaiMaksunSaaja = this.kuitti.w || this.kuitti.el || ''
          }
        },
        error: error => {
          this._errorHandler.handleError(error)
        },
      })

    this.donePercentageObservable = this._uploadTasksSubject.pipe(
      switchMap(tasks => {
        if (!tasks || tasks.length < 1) {
          return observableOf<number>(null)
        }
        return this._tiedostojenLataamisService.annaKokonaisprosenttiSimplified(tasks).pipe(
          tap(percentage => {
            if (percentage > 99.99) {
              this.virheviestiLatausOnKesken = null
            }
          }),
        )
      }),
    )
    this.latausvirheetObservable = this._uploadTasksSubject.pipe(switchMap(tasks => this._tiedostojenLataamisService.annaKaikkiVirheetSimplified(tasks)))

    this.alvPortaatObservable = combineLatest([arvonMuutokset(this.maksutapa, { injector: this._injector }).pipe(startWith(this.kuitti.maksutapa)), this._kayttajaService.kayttajaObservable]).pipe(
      map(([maksutapa, kayttaja]) => {
        // Only shown for ostotositteet
        if (maksutapa === TILIOTETOSITE_MAKSUTAPA_ID || maksutapa === MYYNTITOSITE_MAKSUTAPA_ID || maksutapa === PALKKATOSITE_MAKSUTAPA_ID) {
          return null
        }
        if (!kayttaja?.alvVahennysoikeusPortaat || !kayttaja.prosessinOsat) {
          return null
        }
        const prosessissaAlv =
          kayttaja.prosessinOsat.filter(a => {
            return a.tunniste === TositteenLisaysprosessinOsanTunniste.ALV_VAHENNYSOIKEUS
          }).length > 0
        if (!prosessissaAlv) {
          return null
        }
        const portaat: number[] = []

        let def: number | null = null
        for (const porras of kayttaja.alvVahennysoikeusPortaat) {
          if (!isNaN(porras.arvo)) {
            if (porras.oletus) {
              def = porras.arvo
            }
            portaat.push(porras.arvo)
          }
        }
        portaat.sort((a, b) => a - b)
        return portaat.length > 0 ? { portaat: portaat, oletusProsentti: def } : null
      }),
    )

    this.alvPortaatObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(
      portaatJaOletus => {
        this.oletusAlv = portaatJaOletus?.oletusProsentti
        this.alvPortaat = portaatJaOletus?.portaat
        if (this.oletusAlv && (this.kuitti.alvVahennysoikeus === null || this.kuitti.alvVahennysoikeus === undefined)) {
          asetaArvo(this.alvVahennysoikeus, this.oletusAlv)
        }
      },
      error => {
        this._errorHandler.handleError(error)
      },
    )

    this.isDraggingObservable = this._dndService.isDraggingInWindowObservable
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  valitseMaksutapa(maksutapa: Maksutapa, kuitti: Kuitti) {
    kuitti.p = maksutapa.i
  }
  uploadKuva() {
    this.fileInput.nativeElement.click()
  }

  async fileChanged(event: Event) {
    const target: HTMLInputElement = event.target as HTMLInputElement
    if (!target?.files?.length) {
      return
    }

    const list: FileList = target.files
    const tiedostot: FileDropEntry[] = this._tiedostojenLataamisService.fileListToNgxFileDropEntries(list)

    const voidaanLadata = await this.tiedostoVoidaanLadata(tiedostot)
    if (!voidaanLadata) {
      return
    }
    const kayttaja = await this._kayttajaService.getKayttaja()
    return this.fileDropTiedostoToKuva(tiedostot, kayttaja)
  }

  fileOver(event: Event) {}
  fileLeave(event: Event) {}

  async fileDrop(tiedostot: FileDropEntry[]): Promise<void> {
    this._dndService.setDragging(false)

    const voidaanLadata = await this.tiedostoVoidaanLadata(tiedostot)
    if (!voidaanLadata) {
      return
    }

    const kayttaja = await this._kayttajaService.getKayttaja()
    return this.fileDropTiedostoToKuva(tiedostot, kayttaja)
  }

  async fileDropTiedostoToKuva(tiedostot: FileDropEntry[], kayttaja: Kayttaja) {
    this.virheviestiEiKuvia = null

    for (const tiedosto of tiedostot) {
      const fileEnding = this._tiedostojenLataamisService.getFileEndingFromNgxFileDropEntry(tiedosto)
      const file = await this._tiedostojenLataamisService.getFile(tiedosto.fileEntry as FileSystemFileEntry)

      const alkuperainen: FirestoreTositteenAlkuperainenTiedosto = {
        avain: this._firebaseLemonaid.firestoreCreateId(),
        kuvakansio: this.kuitti.kuvakansio,
        nimi: tiedosto.relativePath,
        fileEnding: fileEnding,
        kasitelty: false,
      }
      this.newAlkuperaisetCache.push(alkuperainen)

      const uploadData = this._tiedostojenLataamisService.tallennaTositetiedostoSimplified(this._firebaseLemonaid, kayttaja.asiakasId + '', alkuperainen, file)
      this._uploadTasksSubject.next([...this._uploadTasksSubject.value, uploadData])

      const kayttajanTiedot: KayttajanTiedot = {
        uid: kayttaja.avain,
        asiakasAvain: kayttaja.asiakasAvain,
        asiakasId: '' + kayttaja.asiakasId,
      }
      const purettuTiedosto: PurettuTiedosto = await this._tositeService.puraYksiTiedostoLokaalisti(
        this.kuitti,
        file,
        tiedosto,
        fileEnding,
        uploadData,
        alkuperainen,
        kayttajanTiedot,
        false,
        Promise.resolve({}),
      )
      if (purettuTiedosto.virhe) {
        this.virheviestiPysyva = this.virheviestiPysyva ? this.virheviestiPysyva + ' ' + purettuTiedosto.virhe : purettuTiedosto.virhe
      }
      if (purettuTiedosto.kasitellytKuvat) {
        if (purettuTiedosto.type === 'img') {
          for (const kuva of purettuTiedosto.kasitellytKuvat) {
            this.newKuvatCache.push(kuva)
            this.kuvatDisplay.push({
              avain: kuva.avain,
              url: this._tositeKuvaCacheService.annaKuvanUrl(kayttajanTiedot, this.kuitti, kuva, 'full'),
              jarjestys: this.kuvatDisplay.length + 1,
              lukittu: !!kuva.lukittu,
            })
            this._changeDetectionRef.markForCheck()
          }
        } else if (purettuTiedosto.type === 'pdf') {
          const pdfKuvat: { avain: string; url: string; jarjestys: number }[] = []
          for (const pdfKuva of purettuTiedosto.kasitellytKuvat) {
            this.newKuvatCache.push(pdfKuva)
            pdfKuvat.push({ avain: pdfKuva.avain, url: this._tositeKuvaCacheService.annaKuvanUrl(kayttajanTiedot, this.kuitti, pdfKuva, 'full'), jarjestys: pdfKuva.jarjestys })
          }
          this.pdfKuvatDisplay.push({ alkuperaisenAvain: alkuperainen.avain, kuvat: pdfKuvat, lukittu: false })
          this._changeDetectionRef.markForCheck()
        }
      }
    }
  }

  poistaKuva(kuvaAvainJaUrl: KuvaAvainJaUrl) {
    const kuvaInCache: FirestoreTositteenKuva = this.newKuvatCache.find(cached => cached.avain === kuvaAvainJaUrl.avain)
    const kuvaInCacheIndex: number = this.newKuvatCache.findIndex(cached => cached.avain === kuvaAvainJaUrl.avain)

    // If the img was just added, delete it from kuvat & alkuperaiset cache. Otherwise mark it as deleted
    if (kuvaInCacheIndex > -1) {
      const cachedAlkuperaisenIndex = this.newAlkuperaisetCache.findIndex(alkup => alkup.avain === kuvaInCache?.alkuperaisenAvain)
      this.newAlkuperaisetCache.splice(cachedAlkuperaisenIndex, 1)
      this.newKuvatCache.splice(kuvaInCacheIndex, 1)
    } else {
      this.poistetuksiMerkitytKuvat.push(kuvaAvainJaUrl.avain) // Remember so as to save deletes later
    }
    // Delete img from displayed kuvat
    const kuvaIndex = this.kuvatDisplay.findIndex(kuva => kuva.avain === kuvaAvainJaUrl.avain)
    if (kuvaIndex > -1) {
      this.kuvatDisplay.splice(kuvaIndex, 1)
    }
  }

  poistaPdf(pdfData: { alkuperaisenAvain: string; kuvat: KuvaAvainUrlJaJarjestys[] }) {
    const alkuperainenInCacheIndex = this.newAlkuperaisetCache.findIndex(cached => cached.avain === pdfData.alkuperaisenAvain)

    // If the PDF was just added, delete it from kuvat & alkuperaiset cache. Otherwise mark its kuvat as deleted
    if (alkuperainenInCacheIndex > -1) {
      this.newAlkuperaisetCache.splice(alkuperainenInCacheIndex, 1)
      this.newKuvatCache = this.newKuvatCache.filter(cached => cached.alkuperaisenAvain !== pdfData.alkuperaisenAvain)
    } else {
      for (const kuva of pdfData.kuvat) {
        this.poistetuksiMerkitytKuvat.push(kuva.avain) // Remember so as to save deletes later
      }
    }
    // Delete PDF from displayed data
    const pdfIndex = this.pdfKuvatDisplay.findIndex(pdf => pdf.alkuperaisenAvain === pdfData.alkuperaisenAvain)
    if (pdfIndex > -1) {
      this.pdfKuvatDisplay.splice(pdfIndex, 1)
    }
  }

  private async tiedostoVoidaanLadata(tiedostot: FileDropEntry[]): Promise<boolean> {
    this.kuvavirheAvain = ''
    this.kuvavirheParametrit = {}

    const supportedImageTypes = ['jpeg', 'jpg', 'png', 'webp', 'tiff', 'gif', 'svg', 'pdf', 'heic', 'heif']
    for (const tiedosto of tiedostot) {
      const file = tiedosto.fileEntry as FileSystemFileEntry

      const fileEnding = this._tiedostojenLataamisService.getFileEndingFromFileName(file.name)
      const fileSize = file ? await this._tiedostojenLataamisService.getFileSize(file) : -1

      if (!fileEnding || supportedImageTypes.indexOf(fileEnding.toLowerCase()) < 0) {
        this.kuvavirheParametrit = {
          tuetutMuodot: supportedImageTypes.join(', '),
        }
        this.kuvavirheAvain = 'kuitit.lataaminen.virheet.vaara-muoto'
        return false
      }

      const maxKoko = 25 * 1024 * 1024
      if (fileSize > maxKoko) {
        const kokoMegatavuissaLokalisoitu = this._currencyService.formatoiDesimaali(fileSize / 1024 / 1024, 2, this._translationService.nykyinenKieli)
        const maxKokoLokalisoitu = this._currencyService.formatoiDesimaali(maxKoko / 1024 / 1024, 2, this._translationService.nykyinenKieli)
        this.kuvavirheParametrit = {
          kokoMax: maxKokoLokalisoitu,
          kokoNyt: kokoMegatavuissaLokalisoitu,
        }
        this.kuvavirheAvain = 'kuitit.lataaminen.virheet.liian-suuri'
        return false
      }
    }

    return true
  }

  private _saveInFlight: boolean
  async save() {
    if (this._saveInFlight) {
      return
    }
    this._saveInFlight = true
    this.virheviestiTallennus = null

    try {
      const lomake = this.lomake
      const onkoTallentaminenEstetty = await this._tositeService.saveIsPreventedSimplified(
        // Palvelu lukee lomakkeelta vain kelpoisuuden
        {
          get valid() {
            return lomake().valid()
          },
        } as Parameters<TositeService['saveIsPreventedSimplified']>[0],
        this.latausvirheetObservable,
        this._uploadTasksSubject.value,
        this.virheviestiPysyva,
      )
      if (onkoTallentaminenEstetty && onkoTallentaminenEstetty.tallennusEstetty) {
        if (onkoTallentaminenEstetty.lataaminenKesken) {
          this.virheviestiLatausOnKesken = this._translationService.lokalisoi('kuitit.muokkaa.validation.ladataan')
        }
        this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
        return
      }
      if (!this.onkoLisattaviaKuvia()) {
        this.virheviestiEiKuvia = this._translationService.lokalisoi('kuitit.muokkaa.validation.ei-kuvia')
        this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
        return
      }

      this._ladataanService.aloitaLataaminen()

      const tositeOliSelvitettava = this.kuitti.x

      // Fill kuitti with the kuva data from newly added images
      for (const cachedAlkuperainen of this.newAlkuperaisetCache) {
        if (!this.kuitti.alkuperaiset) {
          this.kuitti.alkuperaiset = {}
        }
        this.kuitti.alkuperaiset[cachedAlkuperainen.avain] = cachedAlkuperainen
      }
      for (const cachedKuva of this.newKuvatCache) {
        if (!this.kuitti.kuvat) {
          this.kuitti.kuvat = {}
        }
        this.kuitti.kuvat[cachedKuva.avain] = cachedKuva
      }

      for (const merkittyPoistetuksi of this.poistetuksiMerkitytKuvat) {
        if (this.kuitti.kuvat[merkittyPoistetuksi]) {
          this.kuitti.kuvat[merkittyPoistetuksi].poistettu = true
        }
      }

      const sortedImages = Object.values(this.kuitti.kuvat ?? {})
        .filter(k => !k.poistettu)
        .sort((a, b): number => {
          if (a.alkuperaisenAvain !== b.alkuperaisenAvain) {
            if (a.alkuperaisenAvain && b.alkuperaisenAvain) {
              return a.alkuperaisenAvain.localeCompare(b.alkuperaisenAvain)
            } else if (a.alkuperaisenAvain) {
              return 1
            } else {
              return -1
            }
          }
          return a.jarjestys < b.jarjestys ? -1 : 1
        })

      let jarjestys = 1
      for (const image of sortedImages) {
        image.jarjestys = jarjestys
        jarjestys++
      }

      if (this.kuitti.b) {
        delete this.kuitti.b[TositeAutokirjaustyyppi.OMALLA_RAHALLA]
      }

      await this._tositeService.tallennaKuitti(this.kuitti)
      if (this._selvityspyynnonAvain) {
        await this._tositeService.vastaaSelvityspyyntoonTositteenMuokkauksesta(this._selvityspyynnonAvain)
      }

      if (tositeOliSelvitettava && this.onkoVielaSelvitettaviaListalla) {
        // Set last viewed maksutapa to null to preselect selvitettävät at list view
        await this._tositeService.setLastViewedKuitinMaksutapa(null)
        const prevLocation = this._tositeService.getMaksutapaNameForRouter(this.startingMaksutapa)

        this._router.navigate(['tositteet', prevLocation])
      } else {
        // Remember maksutapa to pre-select that when returning
        await this._tositeService.setLastViewedKuitinMaksutapa(this.kuitti.maksutapa)

        this._tositeDatasourceService.changeVuosiKk(this.kuitti.localPvm.year, this.kuitti.localPvm.month - 1)
        const targetLocation = this._tositeService.getMaksutapaNameForRouter(this.kuitti.maksutapa)
        this._router.navigate(['tositteet', targetLocation])
      }
    } catch (error) {
      this.virheviestiTallennus = this._translationService.lokalisoi('tositteet.yhteiset.tallentaminen-epaonnistui')
      this._errorHandler.handleError(error)
    } finally {
      this.poistetuksiMerkitytKuvat = []
      this._saveInFlight = false
      this._ladataanService.lopetaLataaminen()
    }
  }

  async peruuta() {
    this.poistetuksiMerkitytKuvat = []

    if (!this.kuitti.x) {
      // If not selvitettävä, remember maksutapa to pre-select that when returning
      await this._tositeService.setLastViewedKuitinMaksutapa(this.startingMaksutapa)
    } else {
      await this._tositeService.setLastViewedKuitinMaksutapa(null)
    }
    this._tositeDatasourceService.changeVuosiKk(this.startingMonth.year, this.startingMonth.month - 1)
    const targetLocation = this._tositeService.getMaksutapaNameForRouter(this.startingMaksutapa)
    this._router.navigate(['tositteet', targetLocation])
  }
  private setDisplayForSelectedMaksutapa(maksutapaId: number) {
    if (maksutapaId === TILIOTETOSITE_MAKSUTAPA_ID) {
      this.showPvm = true

      this.pvmText = this._translationService.lokalisoi('tositteet.tiliote.muokkaa.pvm')

      // If tiliote, don't show sum, allow only a monthly date
      this.hideSum = true
      this._summaPoissa.set(true)

      this._tositteenPvmPoissa.set(false)
      this.dateSelectionType = 'month'

      // If palkkatosite, don't show sum, show the current day as date (but don't allow change)
    } else if (maksutapaId === PALKKATOSITE_MAKSUTAPA_ID) {
      this.showPvm = false

      this.pvmText = this._translationService.lokalisoi('tositteet.palkka.muokkaa.pvm')

      this.hideSum = true
      this.dateSelectionType = 'day'
      this._summaPoissa.set(true)
      this._tositteenPvmPoissa.set(true)
    } else if (maksutapaId === MYYNTITOSITE_MAKSUTAPA_ID) {
      this.showPvm = true

      this.pvmText = this._translationService.lokalisoi('tositteet.myynti.muokkaa.pvm')
      this.summaText = this._translationService.lokalisoi('tositteet.myynti.muokkaa.summa')

      this._summaPoissa.set(false)
      this.hideSum = false

      this._tositteenPvmPoissa.set(false)
      this.dateSelectionType = 'day'
    } else {
      this.showPvm = true

      // All other maksutavat are under ostot
      this.pvmText = this._translationService.lokalisoi('kuitit.muokkaa.maksupvm')
      this.summaText = this._translationService.lokalisoi('kuitit.muokkaa.summa')

      this._summaPoissa.set(false)
      this.hideSum = false

      this._tositteenPvmPoissa.set(false)
      this.dateSelectionType = 'day'
    }
  }
  virheet<T>(kentta: FieldTree<T>): { [laji: string]: any } | null {
    const virheet = kentta().errors()
    return virheet.length ? Object.fromEntries(virheet.map(virhe => [virhe.kind, virhe])) : null
  }

  private onkoLisattaviaKuvia() {
    return !!this.kuvatDisplay?.length || !!this.pdfKuvatDisplay?.length
  }
}
